/**
 * HeaderProgressWidget — compact streak + XP + badge stats in the top
 * nav, rendered alongside the notification bell.
 *
 * Pulls /api/gamification/{xp,badges,streak} in parallel on mount. If
 * any return 401 (user not signed in) or 4xx, the widget renders null —
 * so it self-hides on /privacy, /terms, /home and for users who can't
 * see learner endpoints. Clicking it deep-links to /progress.
 *
 * Visual intent (2026-06-22 brand refresh): the brand gradient lives
 * on three surfaces only — the top header bar, primary CTAs, and
 * milestone badges. This widget is a milestone surface: streak flame
 * gets a pink→orange gradient stroke, XP number uses the
 * `text-ca-gradient` utility, badge count chip is gradient-filled. The
 * restraint is the point.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { apiGet } from "../lib/api";

interface XpResponse {
  total_xp: number;
  by_source: Record<string, number>;
}

interface BadgesResponse {
  earned_count: number;
  total_count: number;
}

interface StreakResponse {
  current_len: number;
  longest_len: number;
}

interface ProgressSummary {
  xp: number;
  badges_earned: number;
  badges_total: number;
  streak: number;
}

export function HeaderProgressWidget(): React.JSX.Element | null {
  const [summary, setSummary] = useState<ProgressSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [xp, badges, streak] = await Promise.all([
          apiGet<XpResponse>("/api/gamification/xp"),
          apiGet<BadgesResponse>("/api/gamification/badges"),
          apiGet<StreakResponse>("/api/gamification/streak"),
        ]);
        if (cancelled) return;
        // Defensive: if the backend ever changes shape (rename, partial
        // response, anon-fallback returning {}), don't take down the host
        // page. Coerce to numbers, default missing → 0, and self-hide if
        // anything is still not a finite number.
        const next: ProgressSummary = {
          xp: Number(xp?.total_xp ?? 0),
          badges_earned: Number(badges?.earned_count ?? 0),
          badges_total: Number(badges?.total_count ?? 0),
          streak: Number(streak?.current_len ?? 0),
        };
        const allFinite = (Object.values(next) as number[]).every((v) =>
          Number.isFinite(v),
        );
        if (!allFinite) return;
        setSummary(next);
      } catch {
        // Self-hide on any error (401 for unauth, 4xx for non-learner routes).
        // No spinner, no error state — the widget is decorative ambient context,
        // not load-bearing.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!summary) return null;

  return (
    <Link
      to="/progress"
      aria-label={`My Progress — ${summary.streak} day streak, ${summary.xp} XP, ${summary.badges_earned} of ${summary.badges_total} badges`}
      className="hidden items-center gap-3 rounded-full border border-border bg-background/60 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:border-ca-purple/40 hover:bg-ca-gradient-subtle transition-colors lg:flex"
    >
      {/* Streak — flame icon with pink→orange gradient fill */}
      <span className="flex items-center gap-1" title={`${summary.streak} day streak`}>
        <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
          <defs>
            <linearGradient id="streak-flame" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(330 81% 60%)" />
              <stop offset="100%" stopColor="hsl(25 95% 61%)" />
            </linearGradient>
          </defs>
          <path
            fill="url(#streak-flame)"
            d="M12 2c1 3.5 4 5 4 8a4 4 0 0 1-8 0c0-2 1.5-3 1.5-5 0 0 .5 1 1 1 0 0-.5-2 1.5-4Zm-4.5 12a4.5 4.5 0 1 0 9 0c0-2-1-3-1.5-4-.5 1.5-1.5 2-3 2-2 0-2.5-2-2.5-2-.5 1.5-2 2-2 4Z"
          />
        </svg>
        <span className="font-semibold text-foreground">{summary.streak}</span>
      </span>

      {/* XP — number with gradient text */}
      <span className="flex items-center gap-1" title={`${summary.xp} total XP`}>
        <svg viewBox="0 0 24 24" className="h-4 w-4 text-ca-indigo" aria-hidden="true">
          <path
            fill="currentColor"
            d="m12 2 2.39 7.36H22l-6.18 4.49L18.21 21 12 16.51 5.79 21l2.39-7.15L2 9.36h7.61z"
          />
        </svg>
        <span className="text-ca-gradient font-semibold">{summary.xp.toLocaleString()}</span>
      </span>

      {/* Badges — gradient-filled chip */}
      <span
        className="flex items-center gap-1"
        title={`${summary.badges_earned} of ${summary.badges_total} badges earned`}
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4 text-ca-purple" aria-hidden="true">
          <path
            fill="currentColor"
            d="M12 2 8.5 7.5 2 9l4.5 4.5L5 20l7-3.5 7 3.5-1.5-6.5L22 9l-6.5-1.5L12 2z"
          />
        </svg>
        <span className="font-semibold text-foreground">
          {summary.badges_earned}
          <span className="text-muted-foreground">/{summary.badges_total}</span>
        </span>
      </span>
    </Link>
  );
}
